import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError, TRPCLink } from "@trpc/client";
import type { AppRouter } from "cadendar-trpc-server";
import superjson from "superjson";
import { useMemo } from "react";
import { observable } from "@trpc/server/observable";
import ScreenSwitcher from "./ScreenSwitcher.tsx";
import { trpc } from "./trpc.ts";
import { Doctors } from "cadendar-shared";
import { createOrdersReceptionApi } from "../modules/ordering/orderingApi.ts";

const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        throwOnError: (error) => {
          if (error instanceof TRPCClientError) {
            if (error.message === "UNAUTHORIZED") {
              window.location.reload();
            }
          }
          return false;
        },
      },
    },
  });

const hostDirty = import.meta.env.VITE_DEV_HOST || window.location.host;
const clientBuildNumber = VITE_BUILD_NUMBER;
const mode = import.meta.env.MODE;
const prefix = mode === "development" ? "http://" : "https://";
const host = hostDirty.replace("http://", "").replace("https://", "");
const url = `${prefix}${host}/cadendarApi`;

interface MainContainerProps {
  token: string;
  user: Doctors;
}

function getBuildNumberFromResponse(response: Response) {
  const headers = response.headers;
  const build = headers.get("x-build-number");
  console.log("build", build);
  return build;
}

const customLink: TRPCLink<AppRouter> = () => {
  return ({ next, op }) => {
    // this is when passing the result to the next link
    // each link needs to return an observable which propagates results
    return observable((observer) => {
      return next(op).subscribe({
        next(value) {
          const response = value.context?.response as Response | undefined;
          const serverBuildNumber = response
            ? getBuildNumberFromResponse(response)
            : null;
          if (serverBuildNumber && serverBuildNumber !== clientBuildNumber) {
            console.log("force reload");
            console.log("serverBuildNumber", serverBuildNumber);
            console.log("clientBuildNumber", clientBuildNumber);
            window.setTimeout(() => {
              location.reload();
            }, 5000);
          }
          response && getBuildNumberFromResponse(response);
          observer.next(value);
        },
        error(err) {
          observer.error(err);
        },
        complete() {
          observer.complete();
        },
      });
    });
  };
};

const createTrpcClient = (token: string, owner: string) =>
  trpc.createClient({
    links: [
      customLink,
      httpBatchLink({
        url,
        headers() {
          return {
            authorization: `Bearer ${token}`,
            "x-cadendar-owner": owner,
          };
        },
        fetch(url, options) {
          return fetch(url, {
            ...options,
            credentials: "include",
          });
        },
        transformer: superjson,
      }),
    ],
  });

const MainContainer = (props: MainContainerProps) => {
  const trpcClient = useMemo(
    () => createTrpcClient(props.token, props.user.id),
    [props.token, props.user.id],
  );
  const queryClient = useMemo(() => createQueryClient(), [props.user.id]);
  const ordersApi = useMemo(
    () =>
      createOrdersReceptionApi({
        baseUrl: `${prefix}${host}/cadendarOrders`,
        token: props.token,
        onUnauthorized: () => {
          localStorage.removeItem("token");
          window.location.reload();
        },
      }),
    [props.token],
  );
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <ScreenSwitcher
          key={props.user.id}
          userId={props.user.id}
          ordersApi={ordersApi}
        />
      </QueryClientProvider>
    </trpc.Provider>
  );
};

export default MainContainer;
